<script setup>
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { reactive, ref } from 'vue';
import { boolean, object, string } from 'yup';

const {
    tenant,
    googleAdsPlanningKeywordCountries,
    googleAdsPlanningKeywordLanguages,
    googleAdsPlanningKeywordPlanNetworks,
    callback,
} = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    googleAdsPlanningKeywordCountries: {
        required: true,
        type: Array,
    },
    googleAdsPlanningKeywordLanguages: {
        required: true,
        type: Array,
    },
    googleAdsPlanningKeywordPlanNetworks: {
        required: true,
        type: Array,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const isOpen = ref(false);

const form = reactive({
    keywords: '',
    country: null,
    language: null,
    planNetwork: googleAdsPlanningKeywordPlanNetworks[0],
    includeAdult: false,
});

const validationSchema = object({
    keywords: string().required('Ce champ est obligatoire.'),
    country: object().nullable(),
    language: object().nullable(),
    planNetwork: object().required('Ce champ est obligatoire.'),
    includeAdult: boolean().required('Ce champ est obligatoire.'),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.sea.postImportPlanningKeywords', {
                tenant: tenant.id,
            }),
            {
                keywords: form.keywords,
                country: form.country ? form.country.value : null,
                language: form.language ? form.language.value : null,
                planNetwork: form.planNetwork.value,
                includeAdult: form.includeAdult,
            },
        )
        .then(() => {
            isOpen.value = false;

            if (callback) {
                callback();
            }
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <DialogDrawer v-model="isOpen">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Importer </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="1">
                            <TextareaField
                                v-model="form.keywords"
                                label="Mots-clés"
                                legend="Un mot-clé par ligne"
                                field-name="keywords"
                                field-id="keywords"
                                type="keywords"
                                required
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <ListboxField
                                v-model="form.country"
                                field-id="country"
                                field-name="country"
                                label="Pays"
                                :options="googleAdsPlanningKeywordCountries"
                                has-empty-option
                            >
                                <template #buttonContent>
                                    {{ form.country.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.language"
                                field-id="language"
                                field-name="language"
                                label="Langue"
                                :options="googleAdsPlanningKeywordLanguages"
                                has-empty-option
                            >
                                <template #buttonContent>
                                    {{ form.language.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <ListboxField
                                v-model="form.planNetwork"
                                field-id="planNetwork"
                                field-name="planNetwork"
                                label="Réseau"
                                :options="googleAdsPlanningKeywordPlanNetworks"
                                required
                            >
                                <template #buttonContent>
                                    {{ form.planNetwork.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <SwitchField
                                v-model="form.includeAdult"
                                field-name="includeAdult"
                                field-id="includeAdult"
                                label="Inclure les contenus pour adultes"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    submit-text="Importer"
                    submitting-text="Import en cours..."
                    without-back-button
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
